import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getPost, journalPosts } from "@/data/journal";

function formatJournalDate(isoDate: string) {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

export const Route = createFileRoute("/journal/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Not found — European Wig Luxury" }, { name: "robots", content: "noindex" }] };
    }
    const { post } = loaderData;
    const url = `https://europeanwigluxury.com/journal/${params.slug}`;
    return {
      meta: [
        { title: `${post.title} — European Wig Luxury` },
        { name: "description", content: post.description },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "article:published_time", content: post.date },
      ],
      links: [{ rel: "canonical", href: `https://europeanwigluxury.com/journal/${params.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.description,
            datePublished: post.date,
            dateModified: post.date,
            author: { "@type": "Organization", name: "European Wig Luxury" },
            publisher: { "@type": "Organization", name: "European Wig Luxury" },
            mainEntityOfPage: url,
          }),
        },
      ],
    };
  },
  notFoundComponent: PostNotFound,
  errorComponent: PostError,
  component: PostPage,
});

function PostPage() {
  const { post } = Route.useLoaderData();
  const Body = post.body;
  const related = journalPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <article className="container-luxe max-w-3xl py-16 md:py-24">
      <p className="eyebrow">{post.category}</p>
      <h1 className="mt-3 font-display text-4xl leading-[1.1] md:text-5xl">{post.title}</h1>
      <p className="mt-4 text-sm text-muted-foreground">
        {formatJournalDate(post.date)} &middot; {post.readingTime}
      </p>

      <div className="prose-luxe mt-10">
        <p className="text-lg leading-relaxed text-foreground/80">{post.excerpt}</p>
        <Body />
      </div>

      <section className="mt-20 border-t border-border/70 pt-10">
        <p className="eyebrow">Keep reading</p>
        <ul className="mt-6 grid gap-8 md:grid-cols-3">
          {related.map((r) => (
            <li key={r.slug}>
              <Link to="/journal/$slug" params={{ slug: r.slug }} className="group block">
                <h3 className="font-display text-lg leading-snug transition-colors group-hover:text-bronze">{r.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{r.readingTime}</p>
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-10">
          <Link to="/journal" className="text-sm text-bronze underline underline-offset-4">&larr; All entries</Link>
        </p>
      </section>
    </article>
  );
}

function PostNotFound() {
  return (
    <div className="container-luxe max-w-2xl py-24 text-center">
      <p className="eyebrow">Journal</p>
      <h1 className="mt-3 font-display text-3xl md:text-4xl">Entry not found</h1>
      <p className="mt-4 text-foreground/75">This piece may have moved or been retired.</p>
      <p className="mt-8">
        <Link to="/journal" className="text-bronze underline underline-offset-4">Back to the Journal</Link>
      </p>
    </div>
  );
}

function PostError() {
  return (
    <div className="container-luxe max-w-2xl py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-4 text-foreground/75">Please refresh, or head back to the Journal.</p>
      <p className="mt-8">
        <Link to="/journal" className="text-bronze underline underline-offset-4">Back to the Journal</Link>
      </p>
    </div>
  );
}
